import { useState } from "react";
import { AlertTriangle, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";

const n = (v, w) => `${v} ${w}${v === 1 ? "" : "s"}`;
const ACTIONS = {
  quotes: {
    title: "Delete all quotes", icon: Trash2, path: "/settings/danger/delete-quotes",
    text: "Permanently deletes every quote, with its drawing readings and attached files. Credits, purchases and your settings stay. New quotes keep counting from your last quote number.",
    done: (r) => `Deleted ${n(r.quotes, "quote")}, ${n(r.extractions, "reading")} and ${n(r.files, "file")}.`,
  },
  settings: {
    title: "Reset settings & prices to defaults", icon: RotateCcw, path: "/settings/danger/reset-settings",
    text: "Puts types, costs, specs, glass/grid/arch options, markup, tax, shipping, payment terms and estimate texts back to the defaults of a new company, and deletes all door price lists and component product lines. Company details, logo, team, credits and quotes stay.",
    done: (r) => `Settings reset: ${n(r.types_added, "default type")} restored, ${n(r.price_lists, "price list")} and ${n(r.catalog_items, "catalog item")} removed.`,
  },
};

function ConfirmDialog({ action, onClose, onDone }) {
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const a = ACTIONS[action];
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(a.path, { confirm: word });
      toast.success(a.done(data));
      onDone(action);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid={`danger-dialog-${action}`} className="border-red-500/30 bg-slate-950">
        <DialogHeader>
          <DialogTitle className="font-display text-slate-50">{a.title}?</DialogTitle>
          <DialogDescription className="text-slate-400">{a.text} This can't be undone.</DialogDescription>
        </DialogHeader>
        <label className="text-sm text-slate-300" htmlFor="danger-confirm">Type <span className="font-mono font-bold text-red-300">RESET</span> to confirm</label>
        <Input id="danger-confirm" data-testid="danger-confirm-input" value={word} onChange={(e) => setWord(e.target.value)} autoComplete="off"
          className="font-mono" placeholder="RESET" />
        <DialogFooter>
          <button data-testid="danger-cancel-button" onClick={onClose} disabled={busy}
            className="rounded-full px-4 py-2 text-sm text-slate-300 transition-colors hover:text-slate-50">Cancel</button>
          <button data-testid="danger-confirm-button" onClick={run} disabled={word.trim() !== "RESET" || busy}
            className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-500 disabled:opacity-40">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {a.title}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DangerZone({ onDone }) {
  const [open, setOpen] = useState(null);
  return (
    <section data-testid="danger-zone" className="rounded-2xl border border-red-500/30 bg-red-500/[0.03] p-6">
      <div className="flex items-center gap-2 text-red-300"><AlertTriangle className="h-4 w-4" />
        <h2 className="font-display text-base font-bold md:text-lg">Danger zone</h2></div>
      <p className="mt-1 text-sm text-slate-400">Start fresh. Your credits are never touched.</p>
      <div className="mt-5 flex flex-wrap gap-3">
        {Object.entries(ACTIONS).map(([k, a]) => (
          <button key={k} data-testid={`danger-${k}-button`} onClick={() => setOpen(k)}
            className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-4 py-2 text-sm font-semibold text-red-200 transition-colors hover:bg-red-500/10">
            <a.icon className="h-4 w-4" /> {a.title}
          </button>
        ))}
      </div>
      {open && <ConfirmDialog action={open} onClose={() => setOpen(null)} onDone={onDone} />}
    </section>
  );
}
